import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Cookies from "js-cookie"; 
import { withAuth } from "../../hooks/useAuth";
import Layout from "../../components/ui/Layout";
import { motion } from "framer-motion";
import { authenticatedFetch } from "../../utils/authHelper";
import { FiPackage, FiTruck, FiCheckCircle, FiClock, FiMapPin, FiCreditCard, FiMail } from "react-icons/fi";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ 
    opacity: 1, 
    y: 0,
    transition: { 
      delay: i * 0.1,
      duration: 0.5,
      ease: "easeOut"
    }
  })
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

function OrderDetails() {
  const { orderId } = useParams();

  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  
  useEffect(() => {
    fetchOrderData();
  }, []);

  const fetchOrderData = async () => {
    try {
      const response = await authenticatedFetch(`${process.env.REACT_APP_BACKEND_URI}user/order?stripePaymentId=${orderId}`);
      
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      setOrderData(data);
      setLoading(false);
    } catch (error) {
      setErrorMessage("No order has been found. Please try again later or contact support.");
      setLoading(false);
    }
  };
  
  // Get current order status step
  const getOrderStep = (status) => {
    const steps = {
      'pending': 0,
      'paid': 1,
      'processing': 2,
      'shipped': 3,
      'delivered': 4,
      'cancelled': -1
    };
    return steps[status] || 0;
  };

  if (loading) {
    return (
      <section className="relative min-h-screen bg-gray-50 w-full">
        <Layout>
          <div className="w-full flex flex-col items-center justify-center mt-32">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#406580]"
            ></motion.div>
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { delay: 0.4 } }}
              className="mt-4 text-gray-600 font-medium"
            >
              Caricamento dettagli ordine...
            </motion.p>
          </div>
        </Layout>
      </section>
    );
  }

  if (errorMessage) {
    return (
      <section className="relative min-h-screen bg-gray-50 w-full">
        <Layout>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full flex flex-col items-center justify-center mt-32 px-4"
          >
            <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
              <FiPackage className="mx-auto text-red-500 text-5xl mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Ordine non trovato</h2>
              <p className="text-gray-600 mb-6">{errorMessage}</p>
              <div className="flex justify-center gap-4">
                <Link 
                  to="/profilo" 
                  className="px-4 py-2 bg-[#406580] text-white rounded-lg hover:bg-[#305570] transition-colors"
                >
                  I miei Ordini
                </Link>
                <Link 
                  to="/store" 
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Vai allo Store
                </Link>
              </div>
            </div>
          </motion.div>
        </Layout>
      </section>
    );
  }

  // Determine the currency and corresponding price
  const currencySymbol = orderData.currency === "usd" ? "$" : orderData.currency === "eur" ? "€" : "CHF";
  
  // Calculate total price based on currency
  const calculateTotal = () => {
    return orderData.orderProducts.reduce((total, item) => {
      return total + item.productPrice;
    }, 0).toFixed(2);
  };

  // Calculate order creation date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status info
  const getStatusInfo = (status) => {
    switch(status) {
      case 'pending':
        return { color: 'bg-yellow-100 text-yellow-800', icon: <FiClock />, label: 'Pagamento in Attesa' };
      case 'paid':
        return { color: 'bg-blue-100 text-blue-800', icon: <FiCreditCard />, label: 'Pagamento Ricevuto' };
      case 'processing':
        return { color: 'bg-indigo-100 text-indigo-800', icon: <FiPackage />, label: 'In Lavorazione' };
      case 'shipped':
        return { color: 'bg-purple-100 text-purple-800', icon: <FiTruck />, label: 'Spedito' };
      case 'delivered':
        return { color: 'bg-green-100 text-green-800', icon: <FiCheckCircle />, label: 'Consegnato' };
      case 'cancelled':
        return { color: 'bg-red-100 text-red-800', icon: <FiClock />, label: 'Annullato' };
      default:
        return { color: 'bg-gray-100 text-gray-800', icon: <FiClock />, label: status };
    }
  };

  const statusInfo = getStatusInfo(orderData.status);
  const orderStep = getOrderStep(orderData.status);

  return (
    <section className="relative min-h-screen bg-gray-50 w-full pb-16">
      <Layout>
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="max-w-6xl mx-auto px-4 sm:px-6 mt-32 mb-16"
        >
          {/* Order Header */}
          <motion.div 
            variants={fadeIn}
            custom={0}
            className="bg-white rounded-t-xl shadow-sm p-6 border border-gray-100 mb-1"
          >
            <div className="flex flex-wrap justify-between items-start">
              <div>
                <div className="flex items-center">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 inria-serif-regular">Ordine #{orderData.id}</h1>
                  <div className={`ml-4 px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color} flex items-center`}>
                    <span className="mr-1">{statusInfo.icon}</span>
                    <span>{statusInfo.label}</span>
                  </div>
                </div>
                <p className="text-gray-500 mt-2">Effettuato il {formatDate(orderData.orderCreationTime)}</p>
              </div>
              
              {orderData.status === "pending" && (
                <motion.div 
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="mt-4 sm:mt-0"
                >
                  <Link 
                    to={`/payment/${orderId}`} 
                    className="px-4 py-2 bg-[#406580] text-white rounded-lg inline-flex items-center font-medium hover:bg-[#305570] transition-colors"
                  >
                    <FiCreditCard className="mr-2" />
                    Completa Pagamento
                  </Link>
                </motion.div>
              )}
            </div>
          </motion.div>
          
          {/* Order Progress */}
          {orderData.status !== 'cancelled' && (
            <motion.div
              variants={fadeIn}
              custom={1}
              className="bg-white shadow-sm p-6 border border-gray-100 mb-1"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Stato dell'Ordine</h2>
              <div className="relative">
                {/* Progress bar */}
                <div className="h-1 bg-gray-200 rounded-full w-full absolute top-7">
                  <motion.div 
                    className="h-full bg-[#406580] rounded-full"
                    initial={{ width: "0%" }}
                    animate={{ 
                      width: orderStep === 0 ? "10%" : 
                             orderStep === 1 ? "30%" : 
                             orderStep === 2 ? "60%" : 
                             orderStep === 3 ? "85%" : "100%" 
                    }}
                    transition={{ duration: 1, delay: 0.5 }}
                  />
                </div>
                
                {/* Steps */}
                <div className="flex justify-between relative z-10">
                  <StatusStep 
                    title="Ordine"
                    isCompleted={orderStep >= 0}
                    isActive={orderStep === 0}
                    icon={<FiClock />}
                  />
                  <StatusStep
                    title="Pagato"
                    isCompleted={orderStep >= 1}
                    isActive={orderStep === 1}
                    icon={<FiCreditCard />}
                  />
                  <StatusStep
                    title="In Lavorazione"
                    isCompleted={orderStep >= 2}
                    isActive={orderStep === 2}
                    icon={<FiPackage />}
                  />
                  <StatusStep
                    title="Spedito"
                    isCompleted={orderStep >= 3}
                    isActive={orderStep === 3}
                    icon={<FiTruck />}
                  />
                  <StatusStep
                    title="Consegnato"
                    isCompleted={orderStep >= 4}
                    isActive={orderStep === 4}
                    icon={<FiCheckCircle />}
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* Order Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
            {/* Shipping Info */}
            <motion.div
              variants={fadeIn}
              custom={2}
              className="bg-white shadow-sm p-6 border border-gray-100 md:col-span-1"
            >
              <div className="flex items-center mb-4">
                <FiMapPin className="text-[#406580] mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Indirizzo di Spedizione</h2>
              </div>
              <p className="text-gray-700">{orderData.shippingAddress}</p>
              
              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="flex items-center mb-4">
                  <FiMail className="text-[#406580] mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">Contatti</h2>
                </div>
                <button className="text-[#406580] font-medium hover:underline flex items-center">
                  <FiMail className="mr-1" /> Assistenza Ordine
                </button>
              </div>
            </motion.div>

            {/* Order Items */}
            <motion.div
              variants={fadeIn}
              custom={3}
              className="bg-white shadow-sm p-6 border border-gray-100 md:col-span-2"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Articoli Ordinati</h2>
              
              <motion.div 
                variants={stagger}
                className="space-y-4"
              >
                {orderData.orderProducts.map((item, index) => (
                  <motion.div 
                    variants={fadeIn}
                    custom={index}
                    key={index} 
                    className="flex items-center p-4 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100"
                  >
                    <div className="w-16 h-16 bg-gray-100 rounded-md overflow-hidden flex-shrink-0">
                      <img
                        src={item.imageUrl.startsWith('http') ? item.imageUrl : `https://res.cloudinary.com/dndm9hex2/image/upload/v1733331162/${item.imageUrl}`}
                        alt={item.productName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    <div className="ml-4 flex-grow">
                      <h3 className="font-medium text-gray-900">{item.productName}</h3>
                      <p className="text-sm text-gray-500 line-clamp-1">{item.productDescription}</p>
                    </div>
                    
                    <div className="ml-4 text-right">
                      <p className="font-semibold text-gray-900">{currencySymbol}{item.productPrice.toFixed(2)}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>

          {/* Order Summary */}
          <motion.div
            variants={fadeIn}
            custom={4}
            className="bg-white rounded-b-xl shadow-sm p-6 border border-gray-100 mt-1"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Riepilogo Ordine</h2>
            
            <div className="space-y-2 max-w-sm ml-auto">
              <div className="flex justify-between text-gray-600">
                <span>Subtotale</span>
                <span>{currencySymbol}{calculateTotal()}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Spedizione</span>
                <span>{currencySymbol}5.99</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>IVA (22%)</span>
                <span>{currencySymbol}{(parseFloat(calculateTotal()) * 0.22).toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold text-lg text-gray-900 pt-2 border-t border-gray-200 mt-2">
                <span>Totale</span>
                <span>{currencySymbol}{(parseFloat(calculateTotal()) + 5.99).toFixed(2)}</span>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="px-5 py-2 bg-[#406580] text-white rounded-lg font-medium hover:bg-[#305570] transition-colors flex items-center"
              >
                {orderData.status === "pending" ? (
                  <>
                    <FiCreditCard className="mr-2" />
                    Completa Pagamento
                  </>
                ) : orderData.status === "shipped" || orderData.status === "processing" ? (
                  <>
                    <FiTruck className="mr-2" />
                    Traccia Spedizione
                  </>
                ) : (
                  <>
                    <FiPackage className="mr-2" />
                    Visualizza Dettagli
                  </>
                )}
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors flex items-center"
              >
                <FiMail className="mr-2" />
                Contatta il Supporto
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      </Layout>
    </section>
  );
}

// Status step component
function StatusStep({ title, isCompleted, isActive, icon }) {
  return (
    <div className="flex flex-col items-center">
      <div 
        className={`w-14 h-14 rounded-full flex items-center justify-center border-2 ${
          isCompleted 
            ? 'bg-[#406580] border-[#406580] text-white' 
            : isActive
              ? 'bg-white border-[#406580] text-[#406580]'
              : 'bg-white border-gray-300 text-gray-400'
        }`}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ 
            type: "spring", 
            stiffness: 260, 
            damping: 20,
            delay: 0.6 
          }}
        >
          {icon}
        </motion.div>
      </div>
      <p className={`mt-2 text-sm ${
        isCompleted || isActive ? 'text-gray-900 font-medium' : 'text-gray-500'
      }`}>{title}</p>
    </div>
  );
}

export default withAuth(OrderDetails);